// Dependencies
import React, { FunctionComponent, useEffect, useRef, useState } from "react";

// Types
import { ActivityDropDownProps, ActivityOptionItemProps } from "./activity-drop-down.types";

// Styles
import {
    Wrapper,
    ActivityDropDownContainer,
    ActivityDropDownContent,
    ActivityDropDownPlaceholderWrapper,
    DropdownOpenedContainer,
    ActivityDropDownOptionItemCompositionsWrapper,
    ActivityOptionItemContainer,
    ActivityDropDownIconWrapper,
    ActivityDropDownIcon,
    ActivityDropDownPlaceholder,
    ActivityOptionItemLabel,
    ActivityDropDownAdditionalContentLabelWrapper,
    ActivityDropDownAdditionalContentIcon,
    ActivityDropDownAdditionalContentLabel,
    ErrorMessageIcon,
    ErrorMessageLabel,
    ErrorMessageWrapper,
    ActivityDropdownNoOptionsLabel,
    ActivityDropdownInputAndOptionsWrapper
} from "./activity-drop-down.styles";

// Components
import { Skeleton } from "component-library/utilities/skeleton";
import { ConditionallyRender } from "component-library/utilities/conditionally-render";
import { ScreenWidthRender } from "component-library/utilities/screen-width-render";
import { BottomSheet } from "components/BottomSheet";
import { ActivityCheckbox } from "components/ActivityCheckbox";

// Assets
import chevronUp from "assets/img/chevron-up.svg";
import chevronDown from "assets/img/chevron-down.svg";
import fail from "assets/img/fail.svg";

// Utils
import { useWindowDimensions } from "hooks/use-window-dimensions";

export const ActivityDropDown: FunctionComponent<ActivityDropDownProps> = ({
    placeholder,
    errorMessage,
    isDisabled = false,
    additionalContentIcon,
    additionalContentLabel,
    shouldDisplayEmptyState,
    optionItemsCompositions,
    inputComposition,
    ...selectDefaultProps
}) => {
    const { width: windowWidth } = useWindowDimensions();

    const dropDownRef = useRef<HTMLDivElement>(null);
    const dropDownOpenedContentRef = useRef<HTMLDivElement>(null);

    const [isOpened, setIsOpened] = useState(false);

    const handleDropdownClick = (event: React.MouseEvent<HTMLDivElement>) => {
        if (isOpened && dropDownOpenedContentRef.current && dropDownOpenedContentRef.current.contains(event.target as Node)) {
            return true;
        }

        return setIsOpened(!isOpened);
    };

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropDownRef.current && !dropDownRef.current.contains(event.target as Node)) {
                setIsOpened(false);
            }
        };

        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setIsOpened(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        document.addEventListener("keydown", handleKeyDown);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, []);

    const handleClickContainer = (event: any) => {
        if (!isDisabled && windowWidth > 768) {
            handleDropdownClick(event);
            return;
        }

        if (!isOpened) {
            setIsOpened(true);
        }
    };

    const dropdownContent = (
        <ActivityDropdownInputAndOptionsWrapper>
            {inputComposition}

            <ConditionallyRender
                shouldRender={!shouldDisplayEmptyState}
                content={<ActivityDropDownOptionItemCompositionsWrapper>{optionItemsCompositions}</ActivityDropDownOptionItemCompositionsWrapper>}
            />

            <ConditionallyRender
                shouldRender={!!shouldDisplayEmptyState}
                content={<ActivityDropdownNoOptionsLabel>Nenhuma opção encontrada</ActivityDropdownNoOptionsLabel>}
            />
        </ActivityDropdownInputAndOptionsWrapper>
    );

    return (
        <Wrapper ref={dropDownRef}>
            <select
                {...selectDefaultProps}
                style={{
                    display: "none"
                }}
            />
            <ActivityDropDownContainer className="webapp-dropdown" isOpened={isOpened} isDisabled={isDisabled} onClick={handleClickContainer}>
                <ActivityDropDownContent>
                    <ActivityDropDownPlaceholderWrapper>
                        <ActivityDropDownPlaceholder>{placeholder}</ActivityDropDownPlaceholder>
                    </ActivityDropDownPlaceholderWrapper>

                    <ActivityDropDownIconWrapper>
                        <ActivityDropDownIcon src={isOpened ? chevronUp : chevronDown} />
                    </ActivityDropDownIconWrapper>
                </ActivityDropDownContent>

                <ScreenWidthRender
                    renderingWidth={768}
                    actionAfterRenderingWidth="show"
                    content={
                        <ConditionallyRender
                            shouldRender={!!isOpened}
                            content={<DropdownOpenedContainer ref={dropDownOpenedContentRef}>{dropdownContent}</DropdownOpenedContainer>}
                        />
                    }
                />

                <ScreenWidthRender
                    renderingWidth={768}
                    actionAfterRenderingWidth="hide"
                    content={
                        <ConditionallyRender
                            shouldRender={!!isOpened}
                            content={<BottomSheet handleOutsideClick={() => setIsOpened(false)} label={placeholder} content={dropdownContent} />}
                        />
                    }
                />
            </ActivityDropDownContainer>

            <ConditionallyRender
                shouldRender={!!errorMessage}
                content={
                    <ErrorMessageWrapper>
                        <ErrorMessageIcon src={fail} />

                        <ErrorMessageLabel>{errorMessage}</ErrorMessageLabel>
                    </ErrorMessageWrapper>
                }
            />

            <ConditionallyRender
                shouldRender={!!additionalContentLabel}
                content={
                    <ActivityDropDownAdditionalContentLabelWrapper>
                        <ActivityDropDownAdditionalContentIcon src={additionalContentIcon} />
                        <ActivityDropDownAdditionalContentLabel>{additionalContentLabel}</ActivityDropDownAdditionalContentLabel>
                    </ActivityDropDownAdditionalContentLabelWrapper>
                }
            />
        </Wrapper>
    );
};

export const ActivityOptionItem: FunctionComponent<ActivityOptionItemProps> = ({ label, isActive, isLoading, handleClickOption }) => {
    return (
        <ActivityOptionItemContainer className="pdg-option-item" {...(!!handleClickOption && { onClick: handleClickOption })}>
            <ConditionallyRender shouldRender={!!isLoading} content={<Skeleton height="20" width="20" borderRadius="4" />} />

            <ConditionallyRender shouldRender={!isLoading} content={<ActivityCheckbox isChecked={isActive} />} />

            <ActivityOptionItemLabel isActive={!!isActive}>{label}</ActivityOptionItemLabel>

            <ConditionallyRender shouldRender={!!isLoading} content={<Skeleton height="20" width="160" borderRadius="4" />} />
        </ActivityOptionItemContainer>
    );
};
